import {SplitBookingBundle} from '@findhotel/sapi'
import {CalculatedSplitBookingPrice} from 'components/Offer/types'
import {SearchOffer} from 'modules/sapiSearch/slice'
import {stringify} from 'qs'
import {pickBy} from 'ramda'
import {v4 as uuid} from 'uuid'

import {getQueryStringObject, parseUrl} from '@vio/core/src/_web/utils/url'
import {getLinkByType} from '@vio/core/src/offer/business/getLinkByType'
import {ActionLinkTypes} from '@vio/core/src/offer/types/actionLink'
import {Offer} from '@vio/core/src/offer/types/offer'
import {SplitBooking} from '@vio/core/src/room/types/room'

import {RoomOfferUrlError} from './errors'

import type {BaseUrlCheckoutParams} from '@vio/core/src/utils/url/types/UrlParams'

export const getClickedBundleId = (): string => {
  // TODO: improve this in sc-165477
  if (typeof window === 'undefined') return null
  const search = new URLSearchParams(location.search)
  return search.get('clickedBundle')
}

export const getClickedOfferId = (): string => {
  // TODO: improve this in sc-165477
  if (typeof window === 'undefined') return null
  const search = new URLSearchParams(location.search)
  return search.get('clickedOffer')
}

export const isVioOfferOrBundleSelected = (): boolean => {
  return Boolean(getClickedOfferId() || getClickedBundleId())
}

type RoomsSearchParams = Partial<{
  bundleId: string
  clickedOfferId: string
}>

export const getRoomsSearchParamsFromApUrl = (): RoomsSearchParams => {
  // TODO: improve this in sc-165477
  if (typeof window === 'undefined') return {}

  const search = new URLSearchParams(location.search)

  const bundleId = search.get('clickedBundle')
  const clickedOfferId = search.get('clickedOffer')

  const clickedOffer = pickBy(prop => prop !== null, {
    bundleId,
    clickedOfferId
  })
  return bundleId || clickedOfferId ? clickedOffer : undefined
}

/**
 * Getting redirectId either from URL params or generating a new one
 */
export const getApLandingRedirectId = (): string => {
  const urlRedirectId = getQueryStringObject()?.['redirectId']
  return typeof urlRedirectId === 'string' ? urlRedirectId : uuid()
}

interface GetCheckoutLinkParams {
  /**
   * The checkout URL received from the SAPI offer links
   */
  checkoutUrl: string
  /**
   * The params to be used in the checkout URL. SAPI rooms offer already have some params by default but we need to extend them with additional data
   */
  paymentPageParams: Partial<BaseUrlCheckoutParams>
}

/**
 *   Returns single url string composed of an object of params, used for both SB and regular room offers.
 *   Preserves existing query parameters from the base URL and extends them with additional parameters.
 * @param checkoutUrl - The checkout URL received from the SAPI offer links
 * @param paymentPageParams - additional params to be added to the checkout URL
 */
export const getCheckoutLink = ({
  checkoutUrl,
  paymentPageParams
}: GetCheckoutLinkParams): string => {
  try {
    const baseUrl = new URL(checkoutUrl)
    const existingParams = Object.fromEntries(baseUrl.searchParams.entries())

    const queryParams = stringify({
      ...existingParams,
      redirectId: paymentPageParams.redirectId,
      bookingHint: paymentPageParams.bookingHint,
      subBrand: paymentPageParams.subBrand,
      ...(paymentPageParams.gsite && {gsite: paymentPageParams.gsite}),
      ...(paymentPageParams.vf && {vf: paymentPageParams.vf}),
      ...(paymentPageParams.rl && {rl: paymentPageParams.rl})
    })

    const finalCheckoutUrl = `${baseUrl.origin}${baseUrl.pathname}?${queryParams}`
    return finalCheckoutUrl
  } catch (error) {
    Sentry.captureException(
      new RoomOfferUrlError('Room offer URL is invalid', error),
      {extra: {checkoutUrl, paymentPageParams}}
    )
    return undefined
  }
}

export const extractSplitBookingParams = (
  splitBookingBundle: SplitBookingBundle
) => {
  if (!splitBookingBundle) return {}
  return {
    bundleId: splitBookingBundle.bundleId
  }
}

export const extractRegularOfferParams = (offer: SearchOffer) => {
  if (!offer) return {}
  return {
    clickedOfferId: offer.id
  }
}

type OfferCheckOfferParams = {
  offerId?: string
  providerCode: string
  providerRateType: string
  cug: string
}

const roomOfferToOfferCheckParams = (offer: Offer): OfferCheckOfferParams => {
  return {
    offerId: offer.id,
    providerCode: offer.providerCode,
    providerRateType: offer.providerRateType,
    cug: offer.cug[0]
  }
}

const splitBookingOfferToOfferCheckParams = (
  splitBooking: SplitBooking
): OfferCheckOfferParams => {
  return {
    providerCode: splitBooking.offers[0].offer.providerCode,
    providerRateType: splitBooking.offers[0].offer.providerRateType,
    cug: splitBooking.offers[0].offer.cug[0]
  }
}

export const getOfferCheckParams = ({
  offer,
  splitBooking
}: {
  offer?: Offer
  splitBooking?: SplitBooking
}) => {
  if (offer && splitBooking) {
    throw new Error('Offer and sb bundle cannot be present at the same time')
  }
  return offer
    ? roomOfferToOfferCheckParams(offer)
    : splitBookingOfferToOfferCheckParams(splitBooking)
}

export const getOfferTokenParam = (offer?: Offer): string | undefined => {
  if (!offer) return undefined
  const offerCheckoutUrl = getLinkByType(offer.links, ActionLinkTypes.CHECKOUT)
  if (!offerCheckoutUrl) return undefined
  const {
    searchObject: {offer_token: offerToken}
  } = parseUrl(offerCheckoutUrl)

  return (offerToken as string) || undefined
}

export const getPriceValue = (
  offer: SearchOffer,
  showTotalPrices: boolean,
  sbDetails: CalculatedSplitBookingPrice,
  isSplitBooking: boolean
) => {
  const {average: sbPriceNightly, sum: sbPriceTotal} = sbDetails
  const {nightlyRate: offerPriceNightly, totalRate: offerPriceTotal} =
    offer ?? {}

  return isSplitBooking
    ? showTotalPrices
      ? sbPriceTotal
      : sbPriceNightly
    : showTotalPrices
      ? offerPriceTotal
      : offerPriceNightly
}
